import { PORTAL_ZH } from './portalZh';

/**
 * Parent Portal display language (parent-portal-prd.md §13).
 *
 * The English source string is the translation key (gettext style). Navigation
 * config and component copy stay in English; the Chinese catalogue maps each
 * source string to its display text. A missing entry falls back to English.
 */
export const PORTAL_LOCALES = ['en', 'zh'] as const;
export type PortalLocale = (typeof PORTAL_LOCALES)[number];

export const DEFAULT_PORTAL_LOCALE: PortalLocale = 'en';
export const PORTAL_LOCALE_STORAGE_KEY = 'airbotix.portal.locale';

/** `<html lang>` value for each display language. */
export const PORTAL_HTML_LANG: Record<PortalLocale, string> = {
  en: 'en-AU',
  zh: 'zh-CN',
};

/** Number formatting locale for each display language. */
export const PORTAL_NUMBER_LOCALE: Record<PortalLocale, string> = {
  en: 'en-AU',
  zh: 'zh-CN',
};

export type PortalTranslateVars = Record<string, string | number>;
export type PortalTranslate = (text: string, vars?: PortalTranslateVars) => string;

export function isPortalLocale(value: unknown): value is PortalLocale {
  return typeof value === 'string' && (PORTAL_LOCALES as readonly string[]).includes(value);
}

/**
 * Order: the choice made on this device, then the family's saved preferred
 * language, then English. The Portal never infers a language from the browser.
 */
export function resolvePortalLocale(
  deviceChoice: PortalLocale | null,
  familyPreference: string | null | undefined,
): PortalLocale {
  if (deviceChoice) return deviceChoice;
  if (isPortalLocale(familyPreference)) return familyPreference;
  return DEFAULT_PORTAL_LOCALE;
}

/** Storage can throw in private windows or with blocked site data. */
export function readStoredPortalLocale(): PortalLocale | null {
  try {
    const stored = window.localStorage.getItem(PORTAL_LOCALE_STORAGE_KEY);
    return isPortalLocale(stored) ? stored : null;
  } catch {
    return null;
  }
}

export function writeStoredPortalLocale(locale: PortalLocale): void {
  try {
    window.localStorage.setItem(PORTAL_LOCALE_STORAGE_KEY, locale);
  } catch {
    // The choice still applies for this session; it is only not remembered.
  }
}

/**
 * A key may end with `||context` when one English word needs different Chinese
 * in different places: `t('Private||class')`. English shows the text before
 * the marker. The catalogue holds the full key.
 */
const CONTEXT_MARKER = '||';

/** Only a short lower-case tag counts as context, so API prose that has `||` stays whole. */
const CONTEXT_SUFFIX = /\|\|[a-z][a-z ]{0,24}$/;

function withoutContext(text: string): string {
  const at = text.lastIndexOf(CONTEXT_MARKER);
  return at !== -1 && CONTEXT_SUFFIX.test(text) ? text.slice(0, at) : text;
}

function interpolate(template: string, vars?: PortalTranslateVars): string {
  if (!vars) return template;
  return template.replace(/\{(\w+)\}/g, (match, name: string) =>
    name in vars ? String(vars[name]) : match,
  );
}

export function translatePortalText(
  locale: PortalLocale,
  text: string,
  vars?: PortalTranslateVars,
): string {
  const template = locale === 'zh' ? (PORTAL_ZH[text] ?? withoutContext(text)) : withoutContext(text);
  return interpolate(template, vars);
}

export function createPortalTranslate(locale: PortalLocale): PortalTranslate {
  return (text, vars) => translatePortalText(locale, text, vars);
}
